/**
 * DocumentLink Component
 */
import React from "react";
import { filterClass } from "../../utils/class-utils";
import { CardItemIdRoot } from "../external-data/shared/item-id-comment";
import { getBlockAlignClass, resolveHorizontalContentAlign } from "../utils/content-alignment";
import { getDocumentDescription } from "../utils/document-value";
import type { DocumentLinkProps } from "../types";

export function DocumentLink(props: DocumentLinkProps) {
  const { document, paddingClass = "py-0", horizontalAlignClass, inheritedContentAlign, className } = props;
  const resolvedHorizontalAlignClass = getBlockAlignClass(
    resolveHorizontalContentAlign(horizontalAlignClass, inheritedContentAlign, "center")
  );

  const documentDescription = getDocumentDescription(document);

  // Check if we have valid document data (with non-empty values)
  const hasValidData = Boolean(
    documentDescription ||
      (document?.filename && document.filename.trim()) ||
      (document?.url && document.url.trim())
  );

  const fallbackHref =
    document?.filename && document.filename.trim()
      ? `/private/${document.filename.trim()}`
      : "";
  const href = hasValidData ? (document?.url || fallbackHref) : "";
  const name = hasValidData
    ? (documentDescription || document?.filename || "Document")
    : "";
  const isInEditor =
    typeof window !== "undefined" &&
    typeof window.document !== "undefined" &&
    window.document.body?.classList.contains("is-easyblocks-editor");

  const source = document?.filename || document?.url || "";
  const ext = source.split("?")[0].split(".").pop() || "";
  const fileType = ext ? ext.toUpperCase() : "Unknown";

  const containerClassName = [
    filterClass(paddingClass),
  ]
    .filter(Boolean)
    .join(" ");

  const cardClassName = [
    "card",
    "text-bg-white",
    resolvedHorizontalAlignClass,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const cardBodyClassName = "card-body d-flex flex-column gap-2";

  // In live view, hide the component if there's no valid document data
  if (!hasValidData) {
    if (!isInEditor) {
      return null;
    }
    // In edit mode, show a message
    return (
      <div className={containerClassName}>
        <div className={cardClassName} style={{ maxWidth: "420px", width: "100%", "--bs-bg-opacity": ".9" } as React.CSSProperties}>
          <div className={cardBodyClassName}>
            <div className="text-body-secondary small">No document selected. Pick a document to preview.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <CardItemIdRoot
        itemId={document?.item_id ?? document?.id}
        className={cardClassName}
        style={{ maxWidth: "420px", width: "100%", "--bs-bg-opacity": ".9" } as React.CSSProperties}
      >
        <div className={cardBodyClassName}>
          <div className="d-flex align-items-center justify-content-between gap-4">
            <div className="card-title fw-semibold mb-0">{name}</div>
            <span className="text-gray-600 fs-14 fst-italic flex-shrink-0">{fileType}</span>
          </div>
          {href ? (
            <a
              href={href}
              className="btn btn-sm btn-primary text-truncate"
              target="_blank"
              rel="noopener noreferrer"
              aria-describedby="new_window_description"
              onClick={(e) => {
                if (isInEditor) {
                  e.preventDefault();
                }
              }}
            >
              View
            </a>
          ) : (
            <button type="button" className="btn btn-sm btn-outline-secondary text-truncate" disabled>
              View
            </button>
          )}
        </div>
      </CardItemIdRoot>
    </div>
  );
}

export default DocumentLink;
